import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, MoreVertical, Send, RefreshCw, ArrowRight
} from 'lucide-react';

const getInstallmentDates = (installmentsCount) => {
  const dates = [];
  const today = new Date();
  for (let i = 1; i <= installmentsCount; i++) {
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + (i * 7)); // Weekly installments
    dates.push(futureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
  }
  return dates;
};

const getInvestBanksByCountry = (selectedCountry) => {
  const banksMap = {
    Kenya: ['Equity Bank', 'KCB Bank', 'Coop Bank'],
    Uganda: ['Stanbic Bank', 'Centenary Bank', 'DFCU Bank'],
    Tanzania: ['CRDB Bank', 'NMB Bank', 'NBC Bank'],
    Ethiopia: ['CBE Bank', 'Awash Bank', 'Dashen Bank'],
    Rwanda: ['Bank of Kigali', 'BPR Bank', 'Urwego Bank']
  };
  return banksMap[selectedCountry] || ['Partner Bank A', 'Partner Bank B', 'Partner Bank C'];
};

const getMobileProvidersByCountry = (selectedCountry) => {
  const providersMap = {
    Kenya: ['M-Pesa', 'Airtel Money'],
    Uganda: ['MTN MoMo', 'Airtel Money'],
    Tanzania: ['M-Pesa (Vodacom)', 'Airtel Money', 'HaloPesa'],
    Ethiopia: ['Telebirr (Ethio Telecom)'],
    Rwanda: ['MTN MoMo', 'Airtel Money']
  };
  return providersMap[selectedCountry] || ['Mobile Money'];
};

const getPools = (selectedCountry, category) => {
  const poolsMap = {
    Kenya: {
      Bank: [
        { name: 'Equity Bank Pool', rate: 0.075, min: 1000, filled: '85%', size: '1,000,000' },
        { name: 'KCB Bank Pool', rate: 0.082, min: 5000, filled: '62%', size: '2,000,000' },
        { name: 'Coop Bank Pool', rate: 0.068, min: 2000, filled: '45%', size: '1,000,000' }
      ],
      "Deposit taking microfinance": [
        { name: 'Faulu MFI Pool', rate: 0.092, min: 500, filled: '78%', size: '500,000' },
        { name: 'KWFT MFI Pool', rate: 0.088, min: 1000, filled: '91%', size: '500,000' }
      ]
    },
    Uganda: {
      Bank: [
        { name: 'Stanbic Bank Pool', rate: 0.065, min: 10000, filled: '58%', size: '10,000,000' },
        { name: 'Centenary Bank Pool', rate: 0.070, min: 5000, filled: '70%', size: '15,000,000' }
      ],
      "Deposit taking microfinance": [
        { name: 'Pride MFI Pool', rate: 0.085, min: 2000, filled: '82%', size: '5,000,000' },
        { name: 'FINCA Uganda Pool', rate: 0.080, min: 5000, filled: '50%', size: '5,000,000' }
      ]
    },
    Tanzania: {
      Bank: [
        { name: 'CRDB Bank Pool', rate: 0.072, min: 20000, filled: '67%', size: '20,000,000' },
        { name: 'NMB Bank Pool', rate: 0.078, min: 50000, filled: '80%', size: '30,000,000' }
      ],
      "Deposit taking microfinance": [
        { name: 'FINCA Tanzania Pool', rate: 0.086, min: 10000, filled: '75%', size: '10,000,000' }
      ]
    },
    Ethiopia: {
      Bank: [
        { name: 'CBE Pool', rate: 0.058, min: 500, filled: '90%', size: '5,000,000' },
        { name: 'Awash Bank Pool', rate: 0.062, min: 1000, filled: '65%', size: '8,000,000' }
      ],
      "Deposit taking microfinance": [
        { name: 'ACSI Pool', rate: 0.078, min: 200, filled: '85%', size: '2,000,000' }
      ]
    },
    Rwanda: {
      Bank: [
        { name: 'Bank of Kigali Pool', rate: 0.070, min: 10000, filled: '73%', size: '25,000,000' },
        { name: 'BPR Bank Pool', rate: 0.074, min: 5000, filled: '60%', size: '15,000,000' }
      ],
      "Deposit taking microfinance": [
        { name: 'Vision Fund Rwanda Pool', rate: 0.085, min: 2000, filled: '88%', size: '10,000,000' }
      ]
    }
  };

  return poolsMap[selectedCountry]?.[category] || [];
};

const FlowbotDemoEmulator = ({ showHeading = true }) => {
  const [messages, setMessages] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [currentStep, setCurrentStep] = useState('welcome');
  
  // Selection states
  const [country, setCountry] = useState('');
  const [instCategory, setInstCategory] = useState('');
  const [depositTerm, setDepositTerm] = useState('');
  const [depositAmount, setDepositAmount] = useState('10,000');
  const [selectedPool, setSelectedPool] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [mobileProvider, setMobileProvider] = useState('');

  const [investAmount, setInvestAmount] = useState('50,000');
  const [investTenor, setInvestTenor] = useState('30');
  const [investInstallments, setInvestInstallments] = useState('4');
  
  const [isTyping, setIsTyping] = useState(false);
  const messagesRef = useRef(null);

  // Bot response helper
  const addBotMessage = (text, buttons = null) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { type: 'bot', text, buttons }]);
    }, 850);
  };

  // Initialize welcome message
  useEffect(() => {
    setMessages([
      { type: 'bot', text: 'Welcome to Flowbot. I help you find the best T-bill rates from multiple banks or access fixed deposit pools at wholesale rates. What would you like to do today?', buttons: ['Invest', 'Deposit'] }
    ]);
    setCurrentStep('select_action');
  }, []);

  // Scroll to bottom
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleActionClick = (option) => {
    setMessages(prev => [...prev, { type: 'user', text: option }]);

    if (option === 'Invest') {
      setCurrentStep('invest_select_country');
      addBotMessage('Select your country to view T-bill opportunities:', ['Kenya', 'Uganda', 'Tanzania', 'Ethiopia', 'Rwanda']);
    } else if (option === 'Deposit') {
      setCurrentStep('deposit_select_country');
      addBotMessage('Select your country to view available wholesale pools:', ['Kenya', 'Uganda', 'Tanzania', 'Ethiopia', 'Rwanda']);
    } else {
      addBotMessage(`You selected ${option}. For this demo, please select Invest or Deposit to view our interactive flows!`, ['Invest', 'Deposit']);
    }
  };

  const handleCountrySelect = (selectedCountry, flowType) => {
    setMessages(prev => [...prev, { type: 'user', text: selectedCountry }]);
    setCountry(selectedCountry);

    const currency = selectedCountry === 'Kenya' ? 'KES' : selectedCountry === 'Uganda' ? 'UGX' : selectedCountry === 'Tanzania' ? 'TZS' : selectedCountry === 'Ethiopia' ? 'ETB' : 'RWF';

    if (flowType === 'invest') {
      setCurrentStep('ask_amount');
      addBotMessage(`Understood. How much would you like to invest in ${currency}? Enter the amount below:`);
    } else {
      setCurrentStep('deposit_select_type');
      addBotMessage('Would you like to browse pools from Commercial Banks or Deposit Taking Microfinance institutions?', ['Banks', 'Microfinance Banks']);
    }
  };

  const handleDepositTypeSelect = (category) => {
    setMessages(prev => [...prev, { type: 'user', text: category }]);
    setInstCategory(category === 'Banks' ? 'Bank' : 'Deposit taking microfinance');
    setCurrentStep('select_deposit_term');
    addBotMessage('We aggregate retail savings to reach bank wholesale thresholds. What target deposit term would you prefer?', ['30-day Pool', '90-day Pool', '180-day Pool']);
  };

  const handleDepositTermClick = (term) => {
    setMessages(prev => [...prev, { type: 'user', text: term }]);
    setDepositTerm(term);
    setCurrentStep('ask_deposit_amount');
    const currency = country === 'Kenya' ? 'KES' : country === 'Uganda' ? 'UGX' : country === 'Tanzania' ? 'TZS' : country === 'Ethiopia' ? 'ETB' : 'RWF';
    addBotMessage(`Got it. How much ${currency} would you like to save in this pool? Enter the amount below:`);
  };

  const handleDepositPoolSelect = (poolButtonText) => {
    setMessages(prev => [...prev, { type: 'user', text: poolButtonText }]);
    const poolNameClean = poolButtonText.replace('Join ', '');
    const availablePools = getPools(country, instCategory);
    const pool = availablePools.find(p => p.name === poolNameClean) || { name: poolNameClean, rate: 0.08 };
    setSelectedPool(pool);

    setCurrentStep('deposit_select_payment_method');
    addBotMessage(`You selected the ${pool.name}. How would you like to fund this deposit?`, ['Mobile Money', 'Card Payment']);
  };

  const handlePaymentMethodSelect = (method) => {
    setMessages(prev => [...prev, { type: 'user', text: method }]);
    setPaymentMethod(method);

    if (method === 'Mobile Money') {
      setCurrentStep('deposit_select_mobile_provider');
      const providers = getMobileProvidersByCountry(country);
      addBotMessage('Select your mobile money provider:', providers);
    } else {
      setCurrentStep('deposit_stripe_ui');
      addBotMessage('Complete payment details in our simulated Stripe widget:', ['Open Stripe Widget & Pay']);
    }
  };

  const handleMobileProviderSelect = (provider) => {
    setMessages(prev => [...prev, { type: 'user', text: provider }]);
    setMobileProvider(provider);
    setCurrentStep('deposit_simulate_pin');
    
    const currency = country === 'Kenya' ? 'KES' : country === 'Uganda' ? 'UGX' : country === 'Tanzania' ? 'TZS' : country === 'Ethiopia' ? 'ETB' : 'RWF';
    
    addBotMessage(`Sending SDK push via ${provider} gateway...\n\n[SIMULATION] Please enter your 4-digit PIN in the input bar below to approve the deposit of ${currency} ${depositAmount}:`);
  };

  const handleStripePayClick = () => {
    setMessages(prev => [...prev, { type: 'user', text: 'Open Stripe Widget & Pay' }]);
    setCurrentStep('finished');

    const currency = country === 'Kenya' ? 'KES' : country === 'Uganda' ? 'UGX' : country === 'Tanzania' ? 'TZS' : country === 'Ethiopia' ? 'ETB' : 'RWF';
    const depAmountClean = parseInt(depositAmount.replace(/[^0-9]/g, '')) || 10000;
    
    let months = 1;
    if (depositTerm.includes('90-day')) months = 3;
    if (depositTerm.includes('180-day')) months = 6;
    
    const rate = selectedPool?.rate || 0.08;
    const finalAmount = Math.round(depAmountClean * Math.pow(1 + rate, months));
    const totalEarned = finalAmount - depAmountClean;

    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [
        ...prev, 
        { type: 'bot', text: 'Simulating Stripe Card Checkout...\n\nCard Authorized & Payment Captured successfully! 💳' }
      ]);
      
      setTimeout(() => {
        addBotMessage(`Transaction Summary:\n- Principal: ${currency} ${depAmountClean.toLocaleString()}\n- Target Pool: ${selectedPool?.name}\n- Term: ${depositTerm} (${months} Months)\n- Compounding Rate: ${(rate * 100).toFixed(1)}% per month\n\nEstimated Value at Maturity:\n- Maturity Value: ${currency} ${finalAmount.toLocaleString()}\n- Net Interest Earned: ${currency} ${totalEarned.toLocaleString()}\n\nThank you for choosing Forwardsflow to build low-cost retail funding!`);
      }, 1000);
    }, 1200);
  };

  const handleInvestQuoteSelect = (buttonText) => {
    setMessages(prev => [...prev, { type: 'user', text: buttonText }]);
    const selectedBank = buttonText.replace('Accept ', '');
    setCurrentStep('finished');

    const amountClean = parseInt(investAmount.replace(/[^0-9]/g, '')) || 50000;
    const installmentsNum = parseInt(investInstallments.replace(/[^0-9]/g, '')) || 4;
    
    let rate = 0.035;
    if (buttonText.includes('Equity') || buttonText.includes('Stanbic') || buttonText.includes('CRDB') || buttonText.includes('CBE') || buttonText.includes('Kigali')) {
      rate = 0.032;
    } else if (buttonText.includes('Coop') || buttonText.includes('DFCU') || buttonText.includes('NBC') || buttonText.includes('Dashen') || buttonText.includes('Urwego')) {
      rate = 0.038;
    }

    const totalCost = Math.round(amountClean * (1 + rate));
    const installmentAmount = Math.round(totalCost / installmentsNum);
    const dates = getInstallmentDates(installmentsNum);
    const currency = country === 'Kenya' ? 'KES' : country === 'Uganda' ? 'UGX' : country === 'Tanzania' ? 'TZS' : country === 'Ethiopia' ? 'ETB' : 'RWF';

    addBotMessage(`Quote Accepted! Processing T-bill investment with ${selectedBank}...\n\nAgreement Concluded! 📄\n\nPayment Schedule & Terms:\n- Principal Amount: ${currency} ${amountClean.toLocaleString()}\n- Platform Fee: ${(rate * 100).toFixed(1)}% flat\n- Total Investment: ${currency} ${totalCost.toLocaleString()}\n- Installments: ${installmentsNum} Monthly Maturities\n- Amount per Tranche: ${currency} ${installmentAmount.toLocaleString()}\n\nScheduled Due Dates:\n${dates.map((d, index) => `- Installment ${index + 1}: ${d} (${currency} ${installmentAmount.toLocaleString()})`).join('\n')}\n\nYour T-bill allocation has been confirmed. Thank you for choosing Forwardsflow for emerging market investment!`);
  };

  const handleSend = () => {
    if (!inputVal.trim()) return;
    const val = inputVal.trim();
    setInputVal('');

    setMessages(prev => [...prev, { type: 'user', text: val }]);

    if (currentStep === 'ask_amount') {
      setInvestAmount(val);
      setCurrentStep('ask_days');
      addBotMessage('Got it. How many days would you like to repay over?');
    } else if (currentStep === 'ask_days') {
      setInvestTenor(val);
      setCurrentStep('ask_installments');
      addBotMessage('And how many installments would you prefer?');
    } else if (currentStep === 'ask_installments') {
      setInvestInstallments(val);
      setCurrentStep('show_summary');
      
      const amountClean = parseInt(investAmount.replace(/[^0-9]/g, '')) || 50000;
      const installmentsNum = parseInt(val.replace(/[^0-9]/g, '')) || 4;
      const installmentAmount = Math.round(amountClean / installmentsNum);
      const currency = country === 'Kenya' ? 'KES' : country === 'Uganda' ? 'UGX' : country === 'Tanzania' ? 'TZS' : country === 'Ethiopia' ? 'ETB' : 'RWF';

      addBotMessage(`Investment summary:\n\n- Amount: ${currency} ${amountClean.toLocaleString()}\n- Duration: ${investTenor} days\n- Installments: ${installmentsNum}\n- Each payment: about ${currency} ${installmentAmount.toLocaleString()}\n\nBroadcasting to partner banks in ${country}...`);

      setTimeout(() => {
        const banks = getInvestBanksByCountry(country);
        const q1 = Math.round(amountClean * 1.032);
        const q2 = Math.round(amountClean * 1.035);
        const q3 = Math.round(amountClean * 1.038);
        
        setMessages(prev => [...prev, {
          type: 'bot',
          text: `3 quotes received.\n\n1. ${banks[0]}: 3.2% interest (${currency} ${q1.toLocaleString()} total)\n2. ${banks[1]}: 3.5% interest (${currency} ${q2.toLocaleString()} total)\n3. ${banks[2]}: 3.8% interest (${currency} ${q3.toLocaleString()} total)`
        }]);
        
        setCurrentStep('accept_quote');
        setTimeout(() => {
          setMessages(prev => [...prev, {
            type: 'bot',
            buttons: banks.map(b => `Accept ${b}`)
          }]);
        }, 500);
      }, 2000);
    } else if (currentStep === 'ask_deposit_amount') {
      setDepositAmount(val);
      setCurrentStep('show_deposit_pools');

      const currency = country === 'Kenya' ? 'KES' : country === 'Uganda' ? 'UGX' : country === 'Tanzania' ? 'TZS' : country === 'Ethiopia' ? 'ETB' : 'RWF';
      const depAmountClean = parseInt(val.replace(/[^0-9]/g, '')) || 10000;
      
      addBotMessage(`Checking wholesale ${instCategory === 'Bank' ? 'bank' : 'microfinance'} savings pools for a ${currency} ${depAmountClean.toLocaleString()} placement (${depositTerm})...\n\nSince T-bill yields in emerging markets offer attractive returns, our investors benefit from competitive rates.`);

      setTimeout(() => {
        const availablePools = getPools(country, instCategory);
        let textResult = `${availablePools.length} wholesale pools available with high rates:\n\n`;
        availablePools.forEach((p, index) => {
          textResult += `${index + 1}. ${p.name}\n- Rate: ${(p.rate * 100).toFixed(1)}% per month interest\n- Pool Size: ${currency} ${p.size}\n- Filled: ${p.filled}\n\n`;
        });

        setMessages(prev => [...prev, {
          type: 'bot',
          text: textResult
        }]);

        setTimeout(() => {
          setMessages(prev => [...prev, {
            type: 'bot',
            buttons: availablePools.map(p => `Join ${p.name}`)
          }]);
        }, 500);
      }, 2000);
    } else if (currentStep === 'deposit_simulate_pin') {
      setCurrentStep('finished');
      
      const currency = country === 'Kenya' ? 'KES' : country === 'Uganda' ? 'UGX' : country === 'Tanzania' ? 'TZS' : country === 'Ethiopia' ? 'ETB' : 'RWF';
      const depAmountClean = parseInt(depositAmount.replace(/[^0-9]/g, '')) || 10000;
      
      let months = 1;
      if (depositTerm.includes('90-day')) months = 3;
      if (depositTerm.includes('180-day')) months = 6;
      
      const rate = selectedPool?.rate || 0.08;
      const finalAmount = Math.round(depAmountClean * Math.pow(1 + rate, months));
      const totalEarned = finalAmount - depAmountClean;

      addBotMessage(`PIN Accepted. Processing transaction via ${mobileProvider} API...\n\nPayment Successful! 🎉\n\nTransaction Summary:\n- Principal: ${currency} ${depAmountClean.toLocaleString()}\n- Target Pool: ${selectedPool?.name}\n- Term: ${depositTerm} (${months} Months)\n- Compounding Rate: ${(rate * 100).toFixed(1)}% per month\n\nEstimated Value at Maturity:\n- Maturity Value: ${currency} ${finalAmount.toLocaleString()}\n- Net Interest Earned: ${currency} ${totalEarned.toLocaleString()}\n\nThank you for choosing Forwardsflow to build low-cost retail funding!`);
    }
  };

  const handleReset = () => {
    setMessages([
      { type: 'bot', text: 'Welcome to Flowbot. I help you find the best T-bill rates from multiple banks or access fixed deposit pools at wholesale rates. What would you like to do today?', buttons: ['Invest', 'Deposit'] }
    ]);
    setInputVal('');
    setCurrentStep('select_action');
    setCountry('');
    setInstCategory('');
    setDepositTerm('');
    setDepositAmount('10,000');
    setSelectedPool(null);
    setPaymentMethod('');
    setMobileProvider('');
    setInvestAmount('50,000');
    setInvestTenor('30');
    setInvestInstallments('4');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  const isInputDisabled = 
    currentStep === 'select_action' || 
    currentStep === 'invest_select_country' ||
    currentStep === 'deposit_select_country' ||
    currentStep === 'deposit_select_type' ||
    currentStep === 'select_deposit_term' ||
    currentStep === 'accept_quote' || 
    currentStep === 'accept_deposit_quote' ||
    currentStep === 'show_deposit_pools' ||
    currentStep === 'deposit_select_payment_method' ||
    currentStep === 'deposit_select_mobile_provider' ||
    currentStep === 'deposit_stripe_ui' ||
    currentStep === 'show_summary' || 
    currentStep === 'show_deposit_summary' ||
    currentStep === 'finished';

  const getInputPlaceholder = () => {
    if (currentStep === 'ask_amount') return 'Enter amount (e.g. 50,000)';
    if (currentStep === 'ask_days') return 'Enter duration in days (e.g. 30)';
    if (currentStep === 'ask_installments') return 'Enter installments (e.g. 4)';
    if (currentStep === 'ask_deposit_amount') return 'Enter amount (e.g. 10,000)';
    if (currentStep === 'deposit_simulate_pin') return 'Enter 4-Digit PIN';
    return 'Choose an option above...';
  };

  return (
    <div>
      {showHeading && (
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 font-mono">Try FlowBot™ Demo</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-mono">
            Interactive simulator: Click the options or input values to walk through a live T-bill bidding or savings pooling flow.
          </p>
        </div>
      )}

      {/* Crisp & Linear Phone Frame */}
      <div className="max-w-[340px] mx-auto border-[1.5px] border-gray-900 bg-white p-1.5 shadow-none relative">
        <div className="border border-gray-200 flex flex-col h-[560px] relative bg-white">
          
          {/* Telegram Header */}
          <div className="bg-[#527fa6] text-white px-3 py-2.5 flex items-center justify-between z-20">
            <div className="flex items-center gap-2">
              <button 
                onClick={handleReset} 
                className="text-white/80 hover:text-white text-md font-bold"
                title="Reset"
              >
                ←
              </button>
              <div className="w-8 h-8 bg-white/10 rounded-none flex items-center justify-center font-bold text-white text-xs">
                FB
              </div>
              <div>
                <h3 className="font-bold text-xs leading-tight">FlowBot™</h3>
                <p className="text-white/70 text-[9px]">bot • interactive</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <Phone className="w-3.5 h-3.5 cursor-pointer hover:text-white" />
              <MoreVertical className="w-3.5 h-3.5 cursor-pointer hover:text-white" />
            </div>
          </div>

          {/* Telegram Chat Wallpaper Area */}
          <div 
            ref={messagesRef} 
            className="flex-1 p-3 space-y-3 overflow-y-auto relative"
            style={{
              backgroundImage: 'url(/telegram-bg.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {messages.map((msg, idx) => (
              <div key={idx}>
                {msg.type === 'bot' && msg.text && (
                  <div className="flex justify-start mb-2">
                    <div className="bg-white text-gray-900 border border-gray-200 rounded-none px-3 py-2 shadow-none max-w-[85%] text-xs leading-normal font-mono">
                      <p className="whitespace-pre-line">{msg.text}</p>
                    </div>
                  </div>
                )}
                
                {msg.type === 'bot' && msg.buttons && (
                  <div className="flex flex-col gap-1.5 ml-1 mb-2 max-w-[85%]">
                    {msg.buttons.map((btn) => (
                      <button
                        key={btn}
                        onClick={() => {
                          if (currentStep === 'select_action') handleActionClick(btn);
                          else if (currentStep === 'invest_select_country') handleCountrySelect(btn, 'invest');
                          else if (currentStep === 'deposit_select_country') handleCountrySelect(btn, 'deposit');
                          else if (currentStep === 'deposit_select_type') handleDepositTypeSelect(btn);
                          else if (currentStep === 'select_deposit_term') handleDepositTermClick(btn);
                          else if (currentStep === 'show_deposit_pools') handleDepositPoolSelect(btn);
                          else if (currentStep === 'deposit_select_payment_method') handlePaymentMethodSelect(btn);
                          else if (currentStep === 'deposit_select_mobile_provider') handleMobileProviderSelect(btn);
                          else if (currentStep === 'deposit_stripe_ui') handleStripePayClick();
                          else if (currentStep === 'accept_quote') handleInvestQuoteSelect(btn);
                        }}
                        className="px-3 py-2 text-xs text-left bg-white text-[#527fa6] border border-[#527fa6] rounded-none font-mono hover:bg-[#527fa6]/5 transition-all shadow-none"
                      >
                        {btn}
                      </button>
                    ))}
                  </div>
                )}
                
                {msg.type === 'user' && (
                  <div className="flex justify-end mb-2">
                    <div className="bg-[#eeffde] text-gray-900 border border-[#d3ebd0] rounded-none px-3 py-2 shadow-none max-w-[85%] text-xs leading-normal font-mono">
                      <p>{msg.text}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start mb-2">
                <div className="bg-white text-gray-400 border border-gray-200 rounded-none px-3 py-2 text-xs font-mono">
                  bot is typing...
                </div>
              </div>
            )}
          </div>

          {/* Telegram Footer / Input Area */}
          <div className="bg-white border-t border-gray-200 p-2 z-20">
            <div className="flex items-center gap-1.5">
              <div className="flex-1 bg-white rounded-none px-2 py-1.5 border border-gray-300 flex items-center gap-1.5 shadow-none">
                <span className="text-gray-400 text-sm">☺</span>
                <input
                  type="text"
                  placeholder={getInputPlaceholder()}
                  className="flex-1 text-xs bg-transparent border-none outline-none text-gray-950 font-mono"
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isInputDisabled}
                />
                <span className="text-gray-400 text-sm">📎</span>
              </div>
              <button
                onClick={handleSend}
                disabled={isInputDisabled || !inputVal.trim()}
                className={`w-8 h-8 rounded-none flex items-center justify-center text-xs shadow-none border ${
                  isInputDisabled || !inputVal.trim()
                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                    : 'bg-[#527fa6] text-white border-[#527fa6] hover:bg-[#436b8f]'
                }`}
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center justify-between mt-2 px-0.5">
              <button
                onClick={handleReset}
                className="text-[10px] text-gray-500 hover:text-gray-800 flex items-center gap-0.5 font-mono"
              >
                <RefreshCw className="w-2.5 h-2.5" /> Restart Demo
              </button>
              
              {currentStep === 'finished' && (
                <Link
                  to="/"
                  className="text-[#527fa6] text-[10px] font-bold font-mono hover:underline flex items-center gap-0.5"
                >
                  Get Started <ArrowRight className="w-2.5 h-2.5" />
                </Link>
              )}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default FlowbotDemoEmulator;
